/**
 * Modal de geração de avaliações em massa.
 *
 * Inputs:
 *   - Destino: todos / produto específico / coleção
 *   - Total: 1-2000
 *   - Nicho + subnicho (lista fetched do engine)
 *   - Gênero: masculino / feminino / misto
 *   - Distribuição: preset (mostly_5, balanced_high, realistic, only_5) ou custom (5 sliders)
 *   - Compra verificada (checkbox)
 *   - Aprovar automaticamente (checkbox)
 *
 * Pra produto/coleção específicos, usa os pickers modais já existentes
 * (ProductPickerModal / CollectionPickerModal).
 */

import { useEffect, useState } from 'react';
import { useEditor } from '../../state/store';
import {
  fetchReviewNiches,
  generateReviewsApi,
  type Niche,
  type ReviewDistribution,
} from '../../api/client';
import {
  ProductPickerModal,
  CollectionPickerModal,
} from '../pickers/PickerModal';

type DestKind = 'all' | 'product' | 'collection';
type Preset = 'mostly_5' | 'balanced_high' | 'realistic' | 'only_5' | 'custom';

const PRESET_LABELS: Record<Preset, string> = {
  mostly_5: 'Maioria 5 estrelas (80% 5, 15% 4)',
  balanced_high: 'Equilibrado alto (60% 5, 30% 4)',
  realistic: 'Misto realista (50% 5, 30% 4, ~15% 3)',
  only_5: 'Somente 5 estrelas',
  custom: 'Personalizado',
};

const PRESET_DEFAULTS: Record<Exclude<Preset, 'custom'>, Record<1 | 2 | 3 | 4 | 5, number>> = {
  mostly_5: { 5: 80, 4: 15, 3: 4, 2: 1, 1: 0 },
  balanced_high: { 5: 60, 4: 30, 3: 7, 2: 2, 1: 1 },
  realistic: { 5: 50, 4: 30, 3: 12, 2: 5, 1: 3 },
  only_5: { 5: 100, 4: 0, 3: 0, 2: 0, 1: 0 },
};

interface Props {
  onClose: () => void;
  onGenerated: () => void;
}

export function ReviewGeneratorModal({ onClose, onGenerated }: Props) {
  const session = useEditor((s) => s.session);

  const [destKind, setDestKind] = useState<DestKind>('all');
  const [productUuid, setProductUuid] = useState<string>('');
  const [collectionUuid, setCollectionUuid] = useState<string>('');
  const [pickerOpen, setPickerOpen] = useState<DestKind | null>(null);

  const [total, setTotal] = useState(50);

  const [niches, setNiches] = useState<Niche[]>([]);
  const [nicheId, setNicheId] = useState<string>('geral');
  const [subnicheId, setSubnicheId] = useState<string>('');

  const [gender, setGender] = useState<'M' | 'F' | 'mixed'>('mixed');

  const [preset, setPreset] = useState<Preset>('mostly_5');
  const [customDist, setCustomDist] = useState<Record<1 | 2 | 3 | 4 | 5, number>>({
    5: 80, 4: 15, 3: 4, 2: 1, 1: 0,
  });

  const [verified, setVerified] = useState(true);
  const [autoApprove, setAutoApprove] = useState(true);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!session) return;
    fetchReviewNiches(session)
      .then(setNiches)
      .catch(() => {});
  }, [session]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !pickerOpen) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, pickerOpen]);

  const currentNiche = niches.find((n) => n.id === nicheId);
  const subniches = currentNiche?.subniches || [];

  const customSum =
    customDist[1] + customDist[2] + customDist[3] + customDist[4] + customDist[5];

  function resolveDistribution(): ReviewDistribution {
    if (preset === 'custom') return customDist;
    return preset;
  }

  function destinationValid(): boolean {
    if (destKind === 'all') return true;
    if (destKind === 'product') return !!productUuid;
    if (destKind === 'collection') return !!collectionUuid;
    return false;
  }

  async function onGenerate() {
    if (!session) return;
    if (preset === 'custom' && customSum !== 100) {
      setError(`A soma das distribuições deve ser 100 (está em ${customSum})`);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const destination =
        destKind === 'all'
          ? { kind: 'all' as const }
          : destKind === 'product'
            ? { kind: 'product' as const, uuid: productUuid }
            : { kind: 'collection' as const, uuid: collectionUuid };
      const r = await generateReviewsApi(session, {
        destination,
        total,
        niche: nicheId,
        subniche: subnicheId || undefined,
        gender,
        distribution: resolveDistribution(),
        verified,
        auto_approve: autoApprove,
      });
      alert(`Gerados ${r.generated} reviews${r.spreadAcross ? ` em ${r.spreadAcross} produtos` : ''}!`);
      onGenerated();
    } catch (e: any) {
      setError(e?.message || 'Erro ao gerar');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[180] flex items-center justify-center bg-black/60 p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !pickerOpen) onClose(); }}
    >
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <div>
            <h2 className="text-base font-semibold">Gerar avaliações</h2>
            <p className="text-xs text-text-subdued">
              Cria reviews realistas pra popular sua loja. Nomes BR + textos por nicho.
            </p>
          </div>
          <button onClick={onClose} className="text-text-subdued hover:text-text">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {/* Destino */}
          <Section title="Destino">
            <div className="grid grid-cols-3 gap-2">
              {(['all', 'product', 'collection'] as DestKind[]).map((k) => (
                <button
                  key={k}
                  onClick={() => setDestKind(k)}
                  className={
                    'rounded-md border px-3 py-2 text-sm font-medium ' +
                    (destKind === k
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-border text-text-subdued hover:border-text')
                  }
                >
                  {k === 'all' ? 'Todos produtos' : k === 'product' ? 'Produto específico' : 'Coleção inteira'}
                </button>
              ))}
            </div>
            {destKind === 'product' && (
              <div className="mt-2">
                {productUuid ? (
                  <div className="flex items-center justify-between rounded-md border border-border bg-surface px-3 py-2 text-sm">
                    <code className="text-xs">{productUuid.slice(0, 16)}…</code>
                    <button
                      onClick={() => setPickerOpen('product')}
                      className="text-xs font-medium text-primary"
                    >
                      Trocar
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setPickerOpen('product')}
                    className="w-full rounded-md border border-dashed border-border px-3 py-2 text-sm text-text-subdued hover:border-primary hover:text-primary"
                  >
                    Selecionar produto
                  </button>
                )}
              </div>
            )}
            {destKind === 'collection' && (
              <div className="mt-2">
                {collectionUuid ? (
                  <div className="flex items-center justify-between rounded-md border border-border bg-surface px-3 py-2 text-sm">
                    <code className="text-xs">{collectionUuid.slice(0, 16)}…</code>
                    <button
                      onClick={() => setPickerOpen('collection')}
                      className="text-xs font-medium text-primary"
                    >
                      Trocar
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setPickerOpen('collection')}
                    className="w-full rounded-md border border-dashed border-border px-3 py-2 text-sm text-text-subdued hover:border-primary hover:text-primary"
                  >
                    Selecionar coleção
                  </button>
                )}
              </div>
            )}
          </Section>

          {/* Total */}
          <Section title="Quantidade">
            <div className="flex items-center gap-3">
              <input
                type="range"
                min={1}
                max={2000}
                value={total}
                onChange={(e) => setTotal(parseInt(e.target.value, 10))}
                className="flex-1"
              />
              <input
                type="number"
                min={1}
                max={2000}
                value={total}
                onChange={(e) => setTotal(Math.max(1, Math.min(2000, parseInt(e.target.value, 10) || 1)))}
                className="w-20 rounded-md border border-border px-2 py-1 text-sm"
              />
            </div>
            <p className="mt-1 text-xs text-text-subdued">Limite: 2000 reviews por geração.</p>
          </Section>

          {/* Nicho */}
          <Section title="Nicho">
            <div className="grid grid-cols-2 gap-2">
              <select
                value={nicheId}
                onChange={(e) => {
                  setNicheId(e.target.value);
                  setSubnicheId('');
                }}
                className="rounded-md border border-border px-2 py-2 text-sm"
              >
                {niches.map((n) => (
                  <option key={n.id} value={n.id}>{n.label}</option>
                ))}
              </select>
              <select
                value={subnicheId}
                onChange={(e) => setSubnicheId(e.target.value)}
                className="rounded-md border border-border px-2 py-2 text-sm"
                disabled={subniches.length === 0}
              >
                <option value="">— Subnicho (opcional) —</option>
                {subniches.map((s) => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))}
              </select>
            </div>
          </Section>

          {/* Gênero */}
          <Section title="Gênero dos nomes">
            <div className="grid grid-cols-3 gap-2">
              {([
                ['M', 'Masculino'],
                ['mixed', 'Misto'],
                ['F', 'Feminino'],
              ] as const).map(([k, label]) => (
                <button
                  key={k}
                  onClick={() => setGender(k)}
                  className={
                    'rounded-md border px-3 py-2 text-sm font-medium ' +
                    (gender === k
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-border text-text-subdued hover:border-text')
                  }
                >
                  {label}
                </button>
              ))}
            </div>
          </Section>

          {/* Distribuição */}
          <Section title="Distribuição das notas">
            <select
              value={preset}
              onChange={(e) => setPreset(e.target.value as Preset)}
              className="w-full rounded-md border border-border px-2 py-2 text-sm"
            >
              {(Object.keys(PRESET_LABELS) as Preset[]).map((p) => (
                <option key={p} value={p}>{PRESET_LABELS[p]}</option>
              ))}
            </select>
            {preset === 'custom' && (
              <div className="mt-3 space-y-2">
                {([5, 4, 3, 2, 1] as const).map((r) => (
                  <div key={r} className="flex items-center gap-3">
                    <span className="w-8 text-sm font-medium" style={{ color: '#FFA500' }}>
                      {r}★
                    </span>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      value={customDist[r]}
                      onChange={(e) =>
                        setCustomDist((d) => ({ ...d, [r]: parseInt(e.target.value, 10) }))
                      }
                      className="flex-1"
                    />
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={customDist[r]}
                      onChange={(e) =>
                        setCustomDist((d) => ({
                          ...d,
                          [r]: Math.max(0, Math.min(100, parseInt(e.target.value, 10) || 0)),
                        }))
                      }
                      className="w-14 rounded-md border border-border px-1 py-1 text-xs text-right"
                    />
                    <span className="w-4 text-xs text-text-subdued">%</span>
                  </div>
                ))}
                <p
                  className={
                    'mt-1 text-xs ' +
                    (customSum === 100 ? 'text-green-700' : 'text-amber-700')
                  }
                >
                  Soma: {customSum}% {customSum !== 100 && '(deve dar 100)'}
                </p>
              </div>
            )}
          </Section>

          {/* Opções */}
          <Section title="Opções">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={verified}
                onChange={(e) => setVerified(e.target.checked)}
              />
              <span>Marcar como compra verificada</span>
            </label>
            <label className="mt-2 flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={autoApprove}
                onChange={(e) => setAutoApprove(e.target.checked)}
              />
              <span>Aprovar automaticamente (aparecem na loja na hora)</span>
            </label>
          </Section>

          {error && (
            <div className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-border bg-surface px-5 py-3">
          <span className="text-xs text-text-subdued">
            Vai gerar <strong>{total}</strong> review{total === 1 ? '' : 's'}.
          </span>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-white"
            >
              Cancelar
            </button>
            <button
              onClick={onGenerate}
              disabled={busy || !destinationValid()}
              className="rounded-md bg-primary px-4 py-1.5 text-sm font-semibold text-text-inverse hover:opacity-90 disabled:opacity-50"
            >
              {busy ? 'Gerando…' : 'Gerar'}
            </button>
          </div>
        </div>
      </div>

      {pickerOpen === 'product' && (
        <ProductPickerModal
          value={productUuid}
          onChange={(uuid) => setProductUuid(uuid)}
          onClose={() => setPickerOpen(null)}
        />
      )}
      {pickerOpen === 'collection' && (
        <CollectionPickerModal
          value={collectionUuid}
          onChange={(uuid) => setCollectionUuid(uuid)}
          onClose={() => setPickerOpen(null)}
        />
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-4">
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-subdued">
        {title}
      </div>
      {children}
    </div>
  );
}
